import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { patchAdminMeasurementDetails } from '../../api';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { AdminMeasurementIntakeFields } from './AdminMeasurementIntakeFields';

type Props = {
  measurementId: number;
  initialCity?: string;
  initialPreferredTime?: string;
  initialNotes?: string;
  canEdit: boolean;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onSaved: () => void;
};

export function AdminMeasurementDetailsEditor({
  measurementId,
  initialCity = '',
  initialPreferredTime = '',
  initialNotes = '',
  canEdit,
  hapticSelection,
  hapticSuccess,
  onSaved,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [city, setCity] = useState(initialCity);
  const [preferredTime, setPreferredTime] = useState(initialPreferredTime || '');
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCity(initialCity);
    setPreferredTime(initialPreferredTime || '');
    setNotes(initialNotes);
    setEditing(false);
    setError(null);
  }, [measurementId, initialCity, initialPreferredTime, initialNotes]);

  if (!canEdit) {
    return (
      <div className="space-y-1 text-[11px]">
        <p style={{ color: 'var(--text-2)' }}>
          <span className="font-bold" style={{ color: 'var(--cyan)' }}>Адрес: </span>
          {initialCity || '—'}
        </p>
        <p style={{ color: 'var(--text-3)' }}>
          <span className="font-bold">Время: </span>
          {initialPreferredTime || '—'}
        </p>
        {initialNotes ? (
          <p className="leading-snug" style={{ color: 'var(--text-2)' }}>
            <span className="font-bold" style={{ color: '#B794F4' }}>Замерщику: </span>
            {initialNotes}
          </p>
        ) : null}
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="space-y-2">
        <div className="space-y-1 text-[11px]">
          <p style={{ color: 'var(--text-2)' }}>
            <span className="font-bold" style={{ color: 'var(--cyan)' }}>Адрес: </span>
            {city.trim() || 'не указан'}
          </p>
          <p style={{ color: 'var(--text-3)' }}>
            <span className="font-bold">Время: </span>
            {preferredTime.trim() || 'не указано'}
          </p>
          {notes.trim() ? (
            <p className="leading-snug whitespace-pre-wrap" style={{ color: 'var(--text-2)' }}>
              <span className="font-bold" style={{ color: '#B794F4' }}>Замерщику: </span>
              {notes}
            </p>
          ) : (
            <p style={{ color: '#F6AD55' }}>Комментарий для замерщика пустой</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            hapticSelection();
            setCity(initialCity);
            setPreferredTime(initialPreferredTime || '');
            setNotes(initialNotes);
            setError(null);
            setEditing(true);
          }}
          className="press w-full py-2 rounded-lg text-[10px] font-bold touch-manipulation min-h-[40px]"
          style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
        >
          Указать адрес и комментарий
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <AdminMeasurementIntakeFields
        city={city}
        preferredTime={preferredTime}
        notes={notes}
        onCity={setCity}
        onPreferredTime={setPreferredTime}
        onNotes={setNotes}
        disabled={saving}
        onHaptic={hapticSelection}
      />
      {error && (
        <p className="text-[10px] px-2 py-1.5 rounded-lg" style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A' }}>
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => {
            hapticSelection();
            setEditing(false);
            setError(null);
          }}
          className="press flex-1 py-2 rounded-lg text-[10px] font-bold disabled:opacity-50"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' }}
        >
          Отмена
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => {
            hapticSelection();
            void (async () => {
              setSaving(true);
              setError(null);
              try {
                await patchAdminMeasurementDetails(measurementId, {
                  city: city.trim(),
                  preferred_time: preferredTime.trim(),
                  notes: notes.trim(),
                });
                hapticSuccess();
                setEditing(false);
                onSaved();
              } catch (err: unknown) {
                setError(formatAdminPanelError(err, 'Не удалось сохранить'));
              } finally {
                setSaving(false);
              }
            })();
          }}
          className="press flex-1 py-2 rounded-lg text-[10px] font-bold disabled:opacity-50 flex items-center justify-center gap-1"
          style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391' }}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : null}
          Сохранить
        </button>
      </div>
    </div>
  );
}
